import React, { useEffect, useRef, useState } from 'react';
import type { ClientMessage, Credential, PackSummary, ServerMessage, Snapshot } from '../shared/protocol';
import { Button, Dossier } from './design/components/Basics';
import { request, RequestError } from './api';

const sessionKey = 'eu-exam:event-host';
function savedSession(): string {
  try { return localStorage.getItem(sessionKey) ?? ''; } catch { return ''; }
}

export function GameSelect({ packs, value, setValue, disabled = false }: { packs: PackSummary[]; value: string; setValue: (value: string) => void; disabled?: boolean }) {
  return <label className="pack-select">Game<select value={value} disabled={disabled} onChange={e => setValue(e.target.value)}>{packs.map(pack => <option key={pack.id} value={pack.id} disabled={pack.used}>{pack.title}{pack.used ? ' · already played' : ''}</option>)}</select></label>;
}

export function RemoteAudio({ snapshot, online, serverError, send }: { snapshot: Snapshot; online: boolean; serverError: ServerMessage | null; send: (message: ClientMessage) => boolean }) {
  const settings = snapshot.audioSettings ?? { volume: 0.8, muted: false };
  const [volume, setVolume] = useState(settings.volume);
  const pending = useRef<number | null>(null);
  useEffect(() => {
    if (pending.current === null || settings.volume === pending.current) {
      pending.current = null; setVolume(settings.volume);
    }
  }, [settings.volume]);
  useEffect(() => { pending.current = null; setVolume(settings.volume); }, [snapshot.gameId, online, serverError]);
  return <div className="remote-audio">
    <label htmlFor="game-volume">Game volume <output>{Math.round(volume * 100)}%</output></label>
    <input id="game-volume" type="range" min="0" max="100" step="5" value={Math.round(volume * 100)} disabled={!online} onChange={event => {
      const next = Number(event.target.value) / 100;
      pending.current = next; setVolume(next);
      if (!send({ type: 'audio-settings', gameId: snapshot.gameId, volume: next })) { pending.current = null; setVolume(settings.volume); }
    }} />
    <Button variant="paper" aria-pressed={settings.muted} disabled={!online} onClick={() => send({ type: 'audio-settings', gameId: snapshot.gameId, muted: !settings.muted })}>{settings.muted ? 'Unmute' : 'Mute'}</Button>
  </div>;
}

export function HostEntry({ packs, onConnected }: { packs: PackSummary[]; onConnected: (credential: Credential) => void }) {
  const [token, setToken] = useState(savedSession);
  const [pin, setPin] = useState('');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [packId, setPackId] = useState('');
  const connected = useRef(onConnected);
  connected.current = onConnected;
  useEffect(() => {
    if (!token) return;
    const abort = new AbortController();
    setReady(false); setError('');
    request<{ host: Credential | null }>('/api/event/host', { token }, abort.signal).then(result => {
      if (result.host) connected.current(result.host);
      else setReady(true);
    }).catch(e => {
      if (abort.signal.aborted) return;
      if (e instanceof RequestError && e.status === 403) {
        try { localStorage.removeItem(sessionKey); } catch { /* The PIN form still works. */ }
        setToken('');
      }
      setError(e.message);
    });
    return () => abort.abort();
  }, [token, retry]);
  async function unlock(event: React.FormEvent) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const result = await request<{ token: string }>('/api/event/unlock', { pin });
      try { localStorage.setItem(sessionKey, result.token); } catch { /* The remote still works for this visit. */ }
      setPin(''); setToken(result.token);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  const selected = packId || packs[0]?.id || '';
  async function prepare() {
    setBusy(true); setError('');
    try {
      const result = await request<{ host: Credential }>('/api/event/room', { token, packId: selected });
      connected.current(result.host);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  return <Dossier tab="Host remote">
    <h2 className="ex-display">{!token ? 'Unlock your remote.' : ready ? 'Choose the first game.' : 'Restoring your remote…'}</h2>
    {!token ? <form className="host-pin" onSubmit={unlock}>
      <label htmlFor="host-pin">Private host PIN</label>
      <input id="host-pin" className="ex-field" type="password" inputMode="numeric" autoComplete="current-password" maxLength={6} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} required />
      <Button type="submit" variant="paper" block disabled={busy || pin.length !== 6}>{busy ? 'Unlocking…' : 'Unlock remote'}</Button>
    </form> : ready ? <>
      <p>Leave the TV on eu27.pages.dev/tv. It connects automatically.</p>
      <GameSelect packs={packs} value={selected} setValue={setPackId} />
      <Button variant="paper" block disabled={busy || !selected} onClick={prepare}>{busy ? 'Preparing…' : 'Prepare game on TV'}</Button>
      <p className="fine">Keep this phone open to start, pause, skip, adjust sound and choose the next game.</p>
    </> : <p>Keep this page open.</p>}
    {error && <p role="alert">{error}</p>}
    {token && !ready && error && <Button variant="paper" onClick={() => setRetry(value => value + 1)}>Retry connection</Button>}
  </Dossier>;
}

export function EventTv({ onConnected }: { onConnected: (credential: Credential) => void }) {
  const [error, setError] = useState('');
  const connected = useRef(onConnected);
  connected.current = onConnected;
  useEffect(() => {
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await request<{ display: Credential | null }>('/api/event/display', undefined, abort.signal);
        if (abort.signal.aborted) return;
        if (result.display) { connected.current(result.display); return; }
        setError('');
      } catch {
        if (abort.signal.aborted) return;
        setError('Connection interrupted. Reconnecting automatically…');
      }
      timer = setTimeout(poll, 2000);
    };
    void poll();
    return () => { abort.abort(); clearTimeout(timer); };
  }, []);
  return <main className="center"><p className="eyebrow">Canada’s EU Entrance Exam</p><h1 className="ex-display">Waiting for the host.</h1><p>The game will appear here automatically.</p><p className="fine">Leave this page open throughout the event.</p>{error && <p role="status">{error}</p>}</main>;
}
